import { ANNOUNCEMENT, isFeatureCatalogVisible } from 'views/main-page/data/announcement';

/**
 * מעקב מקומי אחרי הכרזות על יכולות חדשות — כמה כניסות לתוכנה עברו מאז שהגרסה
 * הנוכחית עלתה, וכמה פעמים כבר הופנה אוטומטית לדף. נשמר ב-localStorage בנפרד לכל
 * משתמש בחברה (אותם מפתחות session שנשמרים ב-`NKContext`).
 */

const STORAGE_PREFIX = 'nk_whats_new';

/** כמה גרסאות שנראו לשמור בהיסטוריה לפני גריעה */
const MAX_SEEN_HISTORY = 20;

export interface WhatsNewState {
    /** הגרסה שעבורה נספרות הכניסות ב-`entries` */
    version: string;
    /** מספר הכניסות לתוכנה מאז שהגרסה הזו עלתה */
    entries: number;
    /** כמה פעמים כבר הופנה אוטומטית לדף בגרסה הזו */
    shownCount: number;
    /** גרסאות שסיימו את מכסת ההפניות האוטומטיות */
    seen: string[];
}

const emptyState = (): WhatsNewState => ({ version: ANNOUNCEMENT.version, entries: 0, shownCount: 0, seen: [] });

const storageKey = (): string => {
    let company = '';
    let user = '';
    try {
        company = window.localStorage.getItem('company_name') ?? '';
        user = window.localStorage.getItem('user_name') ?? '';
    } catch {
        // localStorage לא זמין (מצב פרטי) — נשתמש במפתח גנרי
    }
    return `${STORAGE_PREFIX}:${company}:${user}`;
};

const shownCountFor = (state: WhatsNewState): number => {
    if (state.version !== ANNOUNCEMENT.version) return 0;
    if (state.seen.includes(ANNOUNCEMENT.version)) return ANNOUNCEMENT.autoShowTimes;
    return state.shownCount;
};

export const readWhatsNewState = (): WhatsNewState => {
    try {
        const raw = window.localStorage.getItem(storageKey());
        if (!raw) return emptyState();
        const parsed = JSON.parse(raw) as Partial<WhatsNewState>;
        const version = typeof parsed.version === 'string' ? parsed.version : ANNOUNCEMENT.version;
        const seen = Array.isArray(parsed.seen) ? parsed.seen.filter((v) => typeof v === 'string') : [];
        const shownCount = Number.isFinite(parsed.shownCount)
            ? Number(parsed.shownCount)
            : (seen.includes(version) ? ANNOUNCEMENT.autoShowTimes : 0);
        return {
            version,
            entries: Number.isFinite(parsed.entries) ? Number(parsed.entries) : 0,
            shownCount,
            seen,
        };
    } catch {
        return emptyState();
    }
};

const writeWhatsNewState = (state: WhatsNewState): void => {
    try {
        window.localStorage.setItem(
            storageKey(),
            JSON.stringify({ ...state, seen: state.seen.slice(-MAX_SEEN_HISTORY) }),
        );
    } catch {
        // אין מה לעשות אם הכתיבה נחסמה — ההכרזה פשוט תופיע שוב בכניסה הבאה
    }
};

/** האם עוד נשארו הפניות אוטומטיות לגרסה הנוכחית */
export const hasUnseenAnnouncement = (): boolean =>
    isFeatureCatalogVisible() && shownCountFor(readWhatsNewState()) < ANNOUNCEMENT.autoShowTimes;

/** סופר הפניה אוטומטית אחת. אחרי `autoShowTimes` הפניות הגרסה נכנסת ל-`seen`. */
export const markAnnouncementSeen = (): void => {
    const state = readWhatsNewState();
    const shownCount = Math.min(shownCountFor(state) + 1, ANNOUNCEMENT.autoShowTimes);
    const seen =
        shownCount >= ANNOUNCEMENT.autoShowTimes && !state.seen.includes(ANNOUNCEMENT.version)
            ? [...state.seen, ANNOUNCEMENT.version]
            : state.seen;
    writeWhatsNewState({ ...state, version: ANNOUNCEMENT.version, shownCount, seen });
};

/**
 * רושם כניסה אחת לתוכנה ומחזיר האם להפנות את המשתמש לדף "מה חדש".
 * החלפת `ANNOUNCEMENT.version` מאפסת את המונה ומתחילה סבב חדש.
 */
export const registerAppEntry = (): { entries: number; shouldShow: boolean } => {
    const previous = readWhatsNewState();
    const sameVersion = previous.version === ANNOUNCEMENT.version;
    const entries = (sameVersion ? previous.entries : 0) + 1;
    const shownCount = sameVersion ? shownCountFor(previous) : 0;
    const state: WhatsNewState = {
        version: ANNOUNCEMENT.version,
        entries,
        shownCount,
        seen: previous.seen,
    };
    writeWhatsNewState(state);

    const shouldShow =
        ANNOUNCEMENT.enabled &&
        isFeatureCatalogVisible() &&
        shownCount < ANNOUNCEMENT.autoShowTimes;

    return { entries, shouldShow };
};
